import {
  Card,
  CardContent,
  Divider,
  Flex,
  FlexProps,
  hooks,
  Skeleton,
  Text,
} from '@bitrise/bitkit';
import { Metric, Session, SessionEvent, TypeMetricKey } from '@trace/types';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';

import dataAtIds from '../../dataAtIds.json';
import { FeatureContext } from '../Features/useFeatures';
import getMockEvents from './SessionEventsVisualisation/getMockEvents';
import SessionEventsVisualisation from './SessionEventsVisualisation/SessionEventsVisualisation';
import SessionMetricVisualisation from './SessionMetricVisualisation/SessionMetricVisualisation';
import SessionTraceVisualisation from './SessionTraceVisualisation/SessionTraceVisualisation';

interface Props extends FlexProps {
  isFetching: boolean;
  metrics: Partial<Record<TypeMetricKey, Metric<'raw'>>>;
  session: Session;
}

const SessionInteractions: FunctionComponent<Props> = ({ isFetching, metrics, session }) => {
  const { hasFeature } = useContext(FeatureContext);
  const match = hooks.useMediaQuery(['1056px']);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [focusX, setFocusX] = useState<null | number>(null);

  useEffect(() => {
    if (session.start && session.end) {
      setEvents(getMockEvents(session.start, session.end));
    }
  }, [session.start, session.end]);

  return (
    <Skeleton active={isFetching} margin="x8">
      <Card
        data-trace-at-id={dataAtIds.SessionExplorerInteractions}
        direction="vertical"
        gap="x8"
        paddingVertical="x8"
      >
        <Text config="5" margin="x8" paddingHorizontal="x8">
          Interactions
        </Text>

        {hasFeature('enable-session-explorer-events') && (
          <>
            <CardContent>
              <Flex>
                <Text ellipsis margin="x4" paddingHorizontal="x8">
                  Events
                </Text>

                <SessionEventsVisualisation
                  end={session.end}
                  events={events}
                  focusX={focusX}
                  onFocusX={setFocusX}
                  start={session.start}
                />
              </Flex>

              <Flex margin="x6" paddingHorizontal="x8">
                <Divider color="gray-3" type="dashed" width="1px" />
              </Flex>
            </CardContent>
          </>
        )}

        <CardContent paddingHorizontal="x8">
          <Flex
            direction={match('1056px') ? 'horizontal' : 'vertical'}
            gap="x8"
            reverse={!match('1056px')}
          >
            <Flex direction="vertical" grow initial={match('1056px') ? 'none' : undefined}>
              <SessionMetricVisualisation
                data-trace-at-id={dataAtIds.SessionExplorerCPUVisualisation}
                end={session.end}
                expanded={isExpanded}
                focusX={focusX}
                height={100}
                metric={metrics.CpuPct}
                metricKey="CpuPct"
                onFocusX={setFocusX}
                start={session.start}
              />
            </Flex>

            <Flex grow initial={match('1056px') ? 'none' : undefined}>
              <SessionMetricVisualisation
                data-trace-at-id={dataAtIds.SessionExplorerMemoryVisualisation}
                end={session.end}
                expanded={isExpanded}
                focusX={focusX}
                height={100}
                metric={metrics.MemoryBytes}
                metricKey="MemoryBytes"
                onExpand={() => setIsExpanded(!isExpanded)}
                onFocusX={setFocusX}
                start={session.start}
              />
            </Flex>
          </Flex>

          <Divider color="gray-3" margin="x6" type="dashed" width="1px" />
        </CardContent>

        <CardContent paddingHorizontal="x8">
          <Flex>
            <SessionTraceVisualisation
              data-trace-at-id={dataAtIds.SessionExplorerTraceVisualisation}
              end={session.end}
              focusX={focusX}
              onFocusX={setFocusX}
              start={session.start}
              traces={session.traces}
            />
          </Flex>
        </CardContent>
      </Card>
    </Skeleton>
  );
};

export default SessionInteractions;
